import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { connectChat, onChatEvent } from "@/lib/chatSocket";
import { alertChat, attachAudioUnlock } from "@/lib/chatNotify";
import { PreviewAsBanner, PreviewAsPicker } from "@/components/PreviewAs";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
    LayoutDashboard,
    Ticket,
    Users,
    UserCog,
    Building2,
    Receipt,
    FileText,
    CheckCircle2,
    LogOut,
    Coins,
    UserCircle2,
    BadgePercent,
    Layers,
    Settings,
    Wallet,
    ScrollText,
    RefreshCw,
    Bell,
    MessageSquare,
    Bot,
    Eye,
    Database,
    Calculator,
    Sword,
    BarChart3,
    Globe,
    HelpCircle,
    Sparkles,
    AtSign,
    Megaphone,
    Headphones,
    Menu,
    X,
    KeyRound,
    ShieldCheck,
    FileSpreadsheet,
    IndianRupee,
    Users2,
    Send,
    FolderTree,
    Briefcase,
    PanelLeftClose, PanelLeftOpen,
    ArrowLeft,
    Layers as LayersIcon,
    ChevronDown, ChevronRight,
} from "lucide-react";

const NAV_ITEMS = {
    admin: [
        // HOME — quick access
        { group: "Home",     to: "/",                  label: "Dashboard",       icon: LayoutDashboard, testid: "nav-dashboard" },
        { group: "Home",     to: "/notices",           label: "Notice Board",    icon: Megaphone,       testid: "nav-notices" },
        { group: "Home",     to: "/kalpana",           label: "Kalpana",         icon: Sparkles,        testid: "nav-kalpana", ownerOnly: true },
        { group: "Home",     to: "/chat",              label: "Message",         icon: MessageSquare,   testid: "nav-chat" },
        { group: "Home",     to: "/client-chat",       label: "Client Message",  icon: Headphones,      testid: "nav-client-chat" },
        { group: "Home",     to: "/notifications",     label: "Notifications",   icon: Bell,            testid: "nav-notifications" },

        // OPS — day-to-day work
        { group: "Ops",      to: "/tickets",           label: "Tickets",         icon: Ticket,          testid: "nav-tickets" },
        { group: "Ops",      to: "/work-orders",       label: "Work Orders",     icon: ScrollText,      testid: "nav-work-orders" },
        { group: "Ops",      to: "/quests",            label: "Quest Board",     icon: Sword,           testid: "nav-quests" },
        { group: "Ops",      to: "/completed-tickets", label: "Completed",       icon: CheckCircle2,    testid: "nav-completed" },
        { group: "Ops",      to: "/documents",         label: "Documents",       icon: FileText,        testid: "nav-documents" },
        { group: "Ops",      to: "/document-feed",     label: "Document Feed",   icon: FileText,        testid: "nav-doc-feed" },
        { group: "Ops",      to: "/compliance",        label: "Compliance",      icon: ShieldCheck,     testid: "nav-compliance" },
        { group: "Ops",      to: "/ai-supervisor",     label: "AI Supervisor",   icon: ShieldCheck,     testid: "nav-ai-supervisor" },
        { group: "Ops",      to: "/activity-log",      label: "Activity Log",    icon: LayersIcon,      testid: "nav-activity-log", ownerOnly: true },

        // CLIENTS — people you work with
        { group: "Clients",  to: "/clients",           label: "Clients",         icon: Users,           testid: "nav-clients" },
        { group: "Clients",  to: "/groups",            label: "Groups",          icon: Users2,          testid: "nav-groups" },
        { group: "Clients",  to: "/families",          label: "Families",        icon: Users2,          testid: "nav-families" },
        { group: "Clients",  to: "/rosters",           label: "Rosters",         icon: Users2,          testid: "nav-rosters" },
        { group: "Clients",  to: "/clients-directory", label: "Client Segments", icon: Layers,          testid: "nav-clients-directory" },
        { group: "Clients",  to: "/vendors",           label: "BAP",             icon: Building2,       testid: "nav-vendors" },
        { group: "Clients",  to: "/leads",             label: "Leads Inbox",     icon: Sparkles,        testid: "nav-leads" },
        { group: "Clients",  to: "/communication-hub", label: "Inbox (Email)",   icon: AtSign,          testid: "nav-inbox" },
        { group: "Clients",  to: "/broadcast-center",  label: "Broadcast Center", icon: Send,           testid: "nav-broadcast" },
        { group: "Clients",  to: "/marketing-cms",     label: "Marketing CMS",   icon: Megaphone,       testid: "nav-cms" },

        // BILLING — money in / money out
        { group: "Billing",  to: "/billing",           label: "Invoices",        icon: Receipt,         testid: "nav-billing" },
        { group: "Billing",  to: "/sales-docs",        label: "Sales Docs",      icon: FileText,        testid: "nav-sales-docs" },
        { group: "Billing",  to: "/receipts",          label: "Receipts",        icon: Wallet,          testid: "nav-receipts" },
        { group: "Billing",  to: "/transactions",      label: "Record Log Book", icon: IndianRupee,     testid: "nav-transactions" },
        { group: "Billing",  to: "/services",          label: "Services",        icon: Layers,          testid: "nav-services" },
        { group: "Billing",  to: "/commissions",       label: "Commissions",     icon: Coins,           testid: "nav-commissions" },
        { group: "Billing",  to: "/expenditures",      label: "Expenditures",    icon: Wallet,          testid: "nav-expenditures" },
        { group: "Billing",  to: "/accounting",        label: "Accounting",      icon: Calculator,      testid: "nav-accounting" },
        { group: "Billing",  to: "/folios",            label: "Folios",          icon: FolderTree,      testid: "nav-folios", external: true },
        { group: "Billing",  to: "/nsdl-report",       label: "NSDL Report",     icon: FileSpreadsheet, testid: "nav-nsdl" },

        // WORKSTATION — compliance apps (per-service tools; each app is offered to clients on a subscription basis)
        { group: "Workstation", to: "/workstation",         label: "Workstation",  icon: Briefcase,       testid: "nav-workstation" },
        { group: "Workstation", to: "/form24g",             label: "Form 24G",     icon: FileSpreadsheet, testid: "nav-form24g" },
        { group: "Workstation", to: "/etds",                label: "eTDS / TDS Filing", icon: FileSpreadsheet, testid: "nav-etds" },
        { group: "Workstation", to: "/gst-returns",         label: "GST Returns",  icon: FileSpreadsheet, testid: "nav-gst-returns-ws" },
        { group: "Workstation", to: "/gst-filings",         label: "GST Filings",  icon: FileSpreadsheet, testid: "nav-gst-filings" },

        // REPORTS
        { group: "Reports",  to: "/reports",           label: "Reports",         icon: BarChart3,       testid: "nav-reports" },
        { group: "Reports",  to: "/vendor-reports",    label: "BAP Reports",     icon: Building2,       testid: "nav-vendor-reports-admin" },

        // ADMIN — firm setup + power tools
        { group: "Admin",    to: "/staff",             label: "Staff",           icon: UserCog,         testid: "nav-staff" },
        { group: "Admin",    to: "/vault",             label: "Vault",           icon: KeyRound,        testid: "nav-vault" },
        { group: "Admin",    to: "/dropdowns",         label: "Dropdown Manager", icon: Database,       testid: "nav-dropdowns" },
        { group: "Admin",    to: "/settings",          label: "Settings",        icon: Settings,        testid: "nav-settings-hub" },
        { group: "Admin",    to: "/company-profile",   label: "Company Profile", icon: Settings,        testid: "nav-company-profile" },
        { group: "Admin",    to: "/admin/whatsapp",    label: "WhatsApp Senders", icon: MessageSquare,  testid: "nav-wa-senders" },
        { group: "Admin",    to: "/admin/vyapar-archive", label: "Vyapar Archive", icon: Database,      testid: "nav-vyapar-archive" },
    ],
    staff: [
        // HOME — daily-driver quick access
        { group: "Home",     to: "/",                  label: "Dashboard",       icon: LayoutDashboard, testid: "nav-dashboard" },
        { group: "Home",     to: "/notifications",     label: "Notifications",   icon: Bell,            testid: "nav-notifications" },
        { group: "Home",     to: "/chat",              label: "Message",         icon: MessageSquare,   testid: "nav-chat" },
        { group: "Home",     to: "/client-chat",       label: "Client Message",  icon: Headphones,      testid: "nav-client-chat" },
        { group: "Home",     to: "/chat-bot",          label: "Message Bot",     icon: Bot,             testid: "nav-chat-bot" },

        // OPS — the work itself
        { group: "Ops",      to: "/tickets",           label: "Tickets",         icon: Ticket,          testid: "nav-tickets" },
        { group: "Ops",      to: "/work-orders",       label: "Work Orders",     icon: ScrollText,      testid: "nav-work-orders" },
        { group: "Ops",      to: "/quests",            label: "Quest Board",     icon: Sword,           testid: "nav-quests" },
        { group: "Ops",      to: "/completed-tickets", label: "Completed",       icon: CheckCircle2,    testid: "nav-completed" },
        { group: "Ops",      to: "/documents",         label: "Documents",       icon: FileText,        testid: "nav-documents" },
        { group: "Ops",      to: "/document-feed",     label: "Document Feed",   icon: FileText,        testid: "nav-doc-feed" },
        { group: "Ops",      to: "/compliance",        label: "Compliance",      icon: ShieldCheck,     testid: "nav-compliance" },

        // CLIENTS — people you work with
        { group: "Clients",  to: "/clients",           label: "Clients",         icon: Users,           testid: "nav-clients" },
        { group: "Clients",  to: "/groups",            label: "Groups",          icon: Users2,          testid: "nav-groups" },
        { group: "Clients",  to: "/families",          label: "Families",        icon: Users2,          testid: "nav-families" },
        { group: "Clients",  to: "/rosters",           label: "Rosters",         icon: Users2,          testid: "nav-rosters" },
        { group: "Clients",  to: "/clients-directory", label: "Client Segments", icon: Layers,          testid: "nav-clients-directory" },
        { group: "Clients",  to: "/vendors",           label: "BAP",             icon: Building2,       testid: "nav-vendors" },
        { group: "Clients",  to: "/leads",             label: "Leads Inbox",     icon: Sparkles,        testid: "nav-leads" },
        { group: "Clients",  to: "/communication-hub", label: "Inbox (Email)",   icon: AtSign,          testid: "nav-inbox" },
        { group: "Clients",  to: "/broadcast-center",  label: "Broadcast Center", icon: Send,           testid: "nav-broadcast" },

        // BILLING — money in / money out
        { group: "Billing",  to: "/billing",           label: "Invoices",        icon: Receipt,         testid: "nav-billing" },
        { group: "Billing",  to: "/receipts",          label: "Receipts",        icon: Wallet,          testid: "nav-receipts" },
        { group: "Billing",  to: "/services",          label: "Services",        icon: Layers,          testid: "nav-services" },
        { group: "Billing",  to: "/expenditures",      label: "Expenditures",    icon: Wallet,          testid: "nav-expenditures" },
        { group: "Billing",  to: "/accounting",        label: "Accounting",      icon: Calculator,      testid: "nav-accounting" },
        { group: "Billing",  to: "/folios",            label: "Folios",          icon: FolderTree,      testid: "nav-folios", external: true },
        { group: "Billing",  to: "/nsdl-report",       label: "NSDL Report",     icon: FileSpreadsheet, testid: "nav-nsdl" },

        // WORKSTATION — compliance filing tools
        { group: "Workstation", to: "/workstation",    label: "Workstation",     icon: Briefcase,       testid: "nav-workstation" },
        { group: "Workstation", to: "/form24g",        label: "Form 24G",        icon: FileSpreadsheet, testid: "nav-form24g" },
        { group: "Workstation", to: "/etds",           label: "eTDS / TDS Filing", icon: FileSpreadsheet, testid: "nav-etds" },
        { group: "Workstation", to: "/gst-returns",    label: "GST Returns",     icon: FileSpreadsheet, testid: "nav-gst-returns-ws" },
        { group: "Workstation", to: "/gst-filings",    label: "GST Filings",     icon: FileSpreadsheet, testid: "nav-gst-filings" },

        // REPORTS
        { group: "Reports",  to: "/reports",           label: "Reports",         icon: BarChart3,       testid: "nav-reports" },

        // TEAM & PERSONAL — team roster + your own docs
        { group: "Team",     to: "/staff",             label: "Team",            icon: UserCog,         testid: "nav-staff" },
        { group: "Team",     to: "/vault",             label: "Vault",           icon: KeyRound,        testid: "nav-vault" },
        { group: "Team",     to: "/my-documents",      label: "My KYC",          icon: FileText,        testid: "nav-my-docs" },
        { group: "Team",     to: "/profile",           label: "My Profile",      icon: UserCircle2,     testid: "nav-profile" },
    ],
    vendor: [
        { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/billing", label: "Bill Register", icon: Receipt, testid: "nav-billing" },
        { to: "/clients", label: "My Clients", icon: Users, testid: "nav-clients" },
        { to: "/vendor-reports", label: "My Reports", icon: BarChart3, testid: "nav-vendor-reports" },
        { to: "/leads", label: "Leads Inbox", icon: Sparkles, testid: "nav-leads" },
        { to: "/commissions", label: "My Commissions", icon: BadgePercent, testid: "nav-commissions" },
        { to: "/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
        { to: "/services", label: "Services", icon: Layers, testid: "nav-services" },
        { to: "/chat", label: "Messages", icon: MessageSquare, testid: "nav-chat" },
        { to: "/needful", label: "Other Needful", icon: HelpCircle, testid: "nav-needful" },
        { to: "/profile", label: "My Profile", icon: UserCircle2, testid: "nav-profile" },
    ],
    client: [
        { to: "/", labelKey: "nav.dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/tickets", labelKey: "nav.my_tickets", label: "My Tickets", icon: Ticket, testid: "nav-tickets" },
        { to: "/services", labelKey: "nav.services", label: "Services", icon: Layers, testid: "nav-services" },
        { to: "/portal/my-services", label: "My Services", icon: Sparkles, testid: "nav-my-services" },
        { to: "/portal/my-price-list", label: "Price List", icon: IndianRupee, testid: "nav-my-price-list" },
        { to: "/portal/my-family", label: "My Family", icon: Users2, testid: "nav-my-family" },
        { to: "/my-invoices", labelKey: "nav.my_invoices", label: "My Invoices", icon: Receipt, testid: "nav-my-invoices" },
        { to: "/my-files", labelKey: "nav.my_files", label: "My Files", icon: FileText, testid: "nav-my-files" },
        { to: "/my-bank-statements", label: "Bank Statements", icon: FileText, testid: "nav-my-bank-statements" },
        { to: "/leads", label: "Refer a Lead", icon: Sparkles, testid: "nav-leads" },
        { to: "/chat", labelKey: "nav.messages", label: "Messages", icon: MessageSquare, testid: "nav-chat" },
        { to: "/needful", label: "Other Needful", icon: HelpCircle, testid: "nav-needful" },
        { to: "/profile", labelKey: "nav.my_profile", label: "My Profile", icon: UserCircle2, testid: "nav-profile" },
    ],
};

const ROLE_COLORS = {
    admin: "#C5A059",
    staff: "#3B82F6",
    vendor: "#8B5CF6",
    client: "#10B981",
};

export default function Layout({ children }) {
    const { user, logout, impersonatedBy } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const role = user?.role || "client";

    // Top-level "home" routes per role — no back button on these.
    const HOME_PATHS = new Set([
        "/", "/dashboard", "/client-portal", "/staff-home",
        "/vendor-portal", "/vendor-dashboard", "/login",
    ]);
    const showBackBtn = !HOME_PATHS.has(location.pathname);

    // Hide the "Made with Emergent" badge for client & vendor portals
    // (mobile + desktop). Staff/admin still see it. The badge carries an
    // inline `display:...!important`, so we must override via JS.
    useEffect(() => {
        const el = document.getElementById("emergent-badge");
        if (!el) return;
        if (["client", "vendor"].includes(role)) {
            el.style.setProperty("display", "none", "important");
        } else {
            el.style.setProperty("display", "inline-flex", "important");
        }
        return () => el.style.setProperty("display", "inline-flex", "important");
    }, [role]);
    const isKalpanaOwner = user?.identifier === "GTWADMIN";
    const rawItems = NAV_ITEMS[role] || NAV_ITEMS.client;
    const items = rawItems.filter((it) => !it.ownerOnly || isKalpanaOwner);
    // Mobile CLIENT portal is intentionally slim (like the original design):
    // Profile · Family · Tickets · View/Upload Documents · Invoices · Chat.
    // Desktop keeps the full client menu.
    const CLIENT_MOBILE_ALLOW = ["/", "/tickets", "/portal/my-family", "/my-files", "/my-invoices", "/chat", "/profile"];
    const mobileDrawerItems = role === "client" ? items.filter((i) => CLIENT_MOBILE_ALLOW.includes(i.to)) : items;
    const clientMobileBar = ["/", "/tickets", "/my-files", "/my-invoices", "/chat", "/profile"]
        .map((p) => items.find((i) => i.to === p)).filter(Boolean);
    const accent = ROLE_COLORS[role];
    const [previewOpen, setPreviewOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    // Sidebar hide/unhide — default HIDDEN. Choice persists per-browser via
    // localStorage so once the user toggles it, it stays that way until they
    // toggle again. Requested by user (Jun 2026).
    const [sidebarHidden, setSidebarHidden] = useState(() => {
        try {
            const v = localStorage.getItem("gtw.sidebarHidden");
            if (v === null) return true;   // default hide
            return v === "1";
        } catch { return true; }
    });
    const toggleSidebar = () => {
        setSidebarHidden((h) => {
            const next = !h;
            try { localStorage.setItem("gtw.sidebarHidden", next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    // Collapsible group state. Per-user (keyed by user.id when available so
    // multiple people on the same browser keep their own layout). Persists in
    // localStorage so a staffer who collapses "Reports" keeps it collapsed
    // across reloads until they expand it themselves.
    const groupsKey = `gtw.navGroupsCollapsed.${user?.id || "anon"}`;
    const [collapsedGroups, setCollapsedGroups] = useState(() => {
        try {
            const raw = localStorage.getItem(groupsKey);
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    const toggleGroup = (name) => {
        setCollapsedGroups((cg) => {
            const next = { ...cg, [name]: !cg[name] };
            try { localStorage.setItem(groupsKey, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };
    const [unread, setUnread] = useState(0);
    const [docsUnread, setDocsUnread] = useState(0);

    // Poll unread chat count every 30s — surface as badge on Messages link
    // + prefix the browser tab title so it's noticeable when minimised.
    useEffect(() => {
        let cancelled = false;
        const baseTitle = "Tax Solution (GTW)";
        const fetchUnread = async () => {
            try {
                const [chatRes, filesRes] = await Promise.allSettled([
                    api.get("/chat/unread-summary"),
                    api.get("/files/unread-summary"),
                ]);
                if (cancelled) return;
                const chatN = Number(chatRes.status === "fulfilled" ? (chatRes.value?.data?.total_unread || 0) : 0);
                const docN = Number(filesRes.status === "fulfilled" ? (filesRes.value?.data?.total_unread || 0) : 0);
                setUnread(chatN);
                setDocsUnread(docN);
                const totalN = chatN + docN;
                document.title = totalN > 0 ? `(${totalN > 99 ? "99+" : totalN}) ${baseTitle}` : baseTitle;
            } catch { /* ignore — auth may not be ready, will retry */ }
        };
        fetchUnread();
        const id = setInterval(fetchUnread, 30000);
        const onFocus = () => fetchUnread();
        window.addEventListener("focus", onFocus);
        return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); document.title = baseTitle; };
    }, []);

    // Realtime chat notifications — WhatsApp-style:
    //   • Distinct ping per origin (client / document / general) + vibrate
    //   • Native Notification when tab hidden
    //   • Toast in-app with "Open" deep link
    //   • Suppressed on /chat (they see the message inline) but unread still bumps
    useEffect(() => {
        if (!user?.id) return;
        connectChat();
        attachAudioUnlock();
        const off = onChatEvent((evt) => {
            if (evt?.type !== "message:new") return;
            const m = evt.message || {};
            const roomType = evt.room_type || "";
            // Suppress own / system / bot messages
            if (!m.sender_id || m.sender_id === user.id) return;
            if (m.is_system || m.is_bot || m.sender_id === "system" || m.sender_id === "bot") return;
            // Client/vendor must not see staff_only notes (backend filters too — defensive)
            if (m.visibility === "staff_only" && !["admin", "staff"].includes(user.role)) return;

            // Did this message carry a document attachment?
            const hasDoc = Array.isArray(m.attachments) && m.attachments.length > 0;
            // Decide ping category — client writes get the loudest tone, then documents, else general.
            const senderIsClient = m.sender_role === "client";
            const pingRole = senderIsClient ? "client" : (hasDoc ? "document" : "general");
            // Decide visual marker on the toast
            const prefix = senderIsClient ? "🔴 " : (hasDoc ? "📎 " : "💬 ");

            const onChatPage = (window.location.pathname || "").startsWith("/chat");
            const onClientChatPage = (window.location.pathname || "").startsWith("/client-chat");
            const userIsViewingThisRoom =
                (onChatPage || onClientChatPage)
                && new URLSearchParams(window.location.search).get("room") === m.room_id;

            // Bump unread only when not already viewing the exact room
            if (!userIsViewingThisRoom) {
                setUnread((n) => n + 1);
                const totalN = (Number(document.title.match(/^\((\d+)\) /)?.[1]) || 0);
                const next = totalN + 1;
                const baseTitle = "Tax Solution (GTW)";
                document.title = `(${next > 99 ? "99+" : next}) ${baseTitle}`;
            }

            // If user is on a chat page broadly (just not THIS room), still skip the toast
            // because Chat.jsx surfaces messages inline. Suppress sound too to avoid double-ping.
            if (onChatPage || onClientChatPage) return;

            // Audio + vibrate per device prefs (respects quiet-hours)
            alertChat(pingRole);

            const senderName = m.sender_name || "Someone";
            const body = (m.text || (m.attachments?.[0]?.name) || "Attachment").slice(0, 120);
            // Different deep-link target depending on room type — client_thread goes to /client-chat
            const targetPath = (roomType === "client_thread")
                ? `/client-chat?room=${m.room_id}`
                : `/chat?room=${m.room_id}`;
            toast.message(`${prefix}${senderName}`, {
                description: body,
                duration: senderIsClient ? 9000 : 6000,
                action: {
                    label: "Open",
                    onClick: () => navigate(targetPath),
                },
            });
            try {
                if (document.visibilityState === "hidden" && "Notification" in window && Notification.permission === "granted") {
                    const n = new Notification(`${prefix}${senderName}`, { body, tag: `chat-${m.room_id}` });
                    n.onclick = () => { window.focus(); navigate(targetPath); n.close(); };
                }
            } catch { /* swallow */ }
        });
        return () => { off?.(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.role]);

    // Show the "Preview as user" launcher only to admins who aren't currently impersonating.
    const canStartPreview = user?.role === "admin" && !impersonatedBy;

    const doLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <>
            <PreviewAsBanner />
            <div className="flex min-h-screen bg-[#F7F7F8]">
            {/* Floating sidebar toggle (visible whether sidebar shown or hidden) */}
            <button
                onClick={toggleSidebar}
                className="hidden md:flex fixed z-40 top-3 items-center justify-center w-9 h-9 rounded-full bg-[#0A1128] text-white border border-[#C5A059] shadow-lg hover:bg-[#1A6177] transition-all"
                style={{ left: sidebarHidden ? "8px" : "236px" }}
                title={sidebarHidden ? "Show sidebar" : "Hide sidebar"}
                data-testid="sidebar-toggle"
            >
                {sidebarHidden ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
            {/* Sidebar */}
            <aside
                data-testid="sidebar"
                className={`${sidebarHidden ? "hidden" : "hidden md:flex"} w-64 shrink-0 flex-col text-white sticky top-0 h-screen`}
                style={{ background: "#0A1128" }}
            >
                {/* Brand */}
                <div className="px-6 py-6 border-b border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 flex items-center justify-center bg-[#1A6177] border border-[#C5A059] rounded-full overflow-hidden shrink-0">
                            <img src="/icons/mascot-192.png" alt="Tax Solution mascot" className="w-14 h-14 object-cover" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading text-lg font-bold tracking-tight leading-tight">
                                Tax Solution <span className="text-[#C5A059]">(GTW)</span>
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.18em] text-[#C5A059]/80 mt-0.5 leading-tight">
                                Powered by<br />
                                <span className="text-white/70 normal-case tracking-wide">Ghatoskash Tax Wizards Pvt&nbsp;Ltd</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Role badge */}
                <div className="px-6 py-4 border-b border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">Signed in as</div>
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-medium truncate flex-1">{user?.name || user?.identifier}</div>
                        {(role === "staff" || role === "admin") && (
                            <span
                                data-testid="star-pill"
                                title={`${user?.stars || 0} stars earned from completed tickets`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#C5A059] to-[#D4B574] text-[#0A1128] text-[11px] font-bold border border-[#C5A059] rounded-full shadow-sm shrink-0"
                            >
                                <span className="text-xs leading-none">★</span>
                                <span className="tabular">{user?.stars || 0}</span>
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-white/50 font-mono mt-0.5">{user?.identifier}</div>
                    <span
                        className="inline-block mt-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]"
                        style={{
                            color: accent,
                            border: `1px solid ${accent}`,
                        }}
                    >
                        {role}
                    </span>
                </div>

                {/* Nav — now also hosts the secondary actions (Preview as User /
                    Refresh / Language / Sign out) inside the same scroll area
                    so they don't pin a 250px-tall footer to the bottom on
                    desktop. Frees up vertical room for the actual menu. */}
                <nav className="flex-1 py-4 overflow-y-auto">
                    {(() => {
                        let lastGroup = null;
                        return items.map((it) => {
                            const { to, labelKey, label, icon: Icon, testid, external, group } = it;
                            const showChatBadge = to === "/chat" && unread > 0;
                            const showDocsBadge = to === "/my-files" && docsUnread > 0;
                            const isNewGroup = group && group !== lastGroup;
                            const groupHeader = isNewGroup ? (
                                <button
                                    key={`hdr-${group}`}
                                    type="button"
                                    onClick={() => toggleGroup(group)}
                                    className="w-full px-6 pt-4 pb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#C5A059]/70 hover:text-[#C5A059] transition-colors group"
                                    data-testid={`nav-group-${group.toLowerCase()}`}
                                    title="Click to collapse / expand this group"
                                >
                                    {collapsedGroups[group]
                                        ? <ChevronRight size={11} className="opacity-70 group-hover:opacity-100" />
                                        : <ChevronDown size={11} className="opacity-70 group-hover:opacity-100" />}
                                    <span>{group}</span>
                                </button>
                            ) : null;
                            if (group) lastGroup = group;
                            // If this item is inside a collapsed group, skip
                            // rendering its NavLink — but STILL emit the header
                            // when we hit a new group.
                            if (group && collapsedGroups[group]) {
                                return groupHeader; // may be null (subsequent items in same collapsed group)
                            }
                            const linkEl = external ? (
                                <a key={to} href={to} data-testid={testid} className="nav-item">
                                    <Icon size={17} strokeWidth={1.75} />
                                    <span className="flex-1">{labelKey ? t(labelKey) : label}</span>
                                </a>
                            ) : (
                                <NavLink
                                    key={to}
                                    to={to}
                                    end={to === "/"}
                                    data-testid={testid}
                                    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                                >
                                    <Icon size={17} strokeWidth={1.75} />
                                    <span className="flex-1">{labelKey ? t(labelKey) : label}</span>
                                    {showChatBadge && (
                                        <span className="ml-auto bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 leading-none rounded-full min-w-[18px] text-center animate-pulse shadow-[0_0_0_2px_rgba(239,68,68,0.4)]" data-testid="nav-chat-unread-badge">
                                            {unread > 99 ? "99+" : unread}
                                        </span>
                                    )}
                                    {showDocsBadge && (
                                        <span className="ml-auto bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 leading-none rounded-full min-w-[18px] text-center animate-pulse shadow-[0_0_0_2px_rgba(245,158,11,0.4)]" data-testid="nav-docs-unread-badge">
                                            {docsUnread > 99 ? "99+" : docsUnread}
                                        </span>
                                    )}
                                </NavLink>
                            );
                            return groupHeader ? (
                                <div key={`${group}-${to}`}>{groupHeader}{linkEl}</div>
                            ) : linkEl;
                        });
                    })()}

                    {/* Subtle divider before the secondary action group */}
                    <div className="mx-4 my-3 border-t border-white/10" />

                    {canStartPreview && (
                        <button
                            data-testid="nav-preview-as"
                            onClick={() => setPreviewOpen(true)}
                            className="nav-item w-full text-left"
                            style={{ borderLeft: "3px solid #F59E0B" }}
                            title="Preview the app as another user (client, staff, vendor)"
                        >
                            <Eye size={17} strokeWidth={1.75} />
                            <span>Preview as User</span>
                        </button>
                    )}
                    <button
                        data-testid="layout-refresh"
                        onClick={() => window.location.reload()}
                        className="nav-item w-full text-left"
                        style={{ borderLeft: "3px solid transparent" }}
                        title="Reload current page (refresh data from server)"
                    >
                        <RefreshCw size={17} strokeWidth={1.75} />
                        <span>Refresh</span>
                    </button>
                    <button
                        data-testid="nav-language"
                        onClick={() => setLangOpen(true)}
                        className="nav-item w-full text-left"
                        style={{ borderLeft: "3px solid #C5A059" }}
                        title="Change language / भाषा बदलें / ভাষা সলনি কৰক"
                    >
                        <Globe size={17} strokeWidth={1.75} />
                        <span>{t("common.language")}</span>
                    </button>
                    <button
                        data-testid="nav-logout"
                        onClick={doLogout}
                        className="nav-item w-full text-left"
                        style={{ borderLeft: "3px solid transparent" }}
                    >
                        <LogOut size={17} strokeWidth={1.75} />
                        <span>Sign out</span>
                    </button>
                    <div className="px-4 pt-3 pb-1 text-[9px] leading-tight text-white/40 font-mono">
                        gtw.co.in · v1.0
                    </div>
                </nav>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A1128] text-white px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {/* Staff on mobile don't get the hamburger — sidebar is hidden entirely.
                        Admins still see it so they can manage from a phone if needed. */}
                    {user?.role !== "staff" && (
                        <button
                            onClick={() => setMobileDrawerOpen(true)}
                            className="p-1.5 -ml-1 rounded hover:bg-white/10 text-[#C5A059]"
                            aria-label="Open menu"
                            data-testid="mobile-menu-btn"
                        >
                            <Menu size={20} />
                        </button>
                    )}
                    <div className="w-9 h-9 flex items-center justify-center bg-[#1A6177] border border-[#C5A059] rounded-full overflow-hidden">
                        <img src="/icons/mascot-192.png" alt="Tax Solution" className="w-9 h-9 object-cover" />
                    </div>
                    <span className="font-heading font-bold">Tax Solution</span>
                </div>
                <div className="flex items-center gap-3">
                    {(role === "staff" || role === "admin") && (
                        <div
                            data-testid="star-pill-mobile"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#C5A059] to-[#D4B574] text-[#0A1128] text-xs font-bold border border-[#C5A059] rounded-full"
                            title={`${user?.stars || 0} stars`}
                        >
                            <span className="text-sm leading-none">★</span>
                            <span className="tabular">{user?.stars || 0}</span>
                        </div>
                    )}
                    <button data-testid="mobile-language" onClick={() => setLangOpen(true)} className="text-[#C5A059]" title="Change language">
                        <Globe size={18} />
                    </button>
                    <button data-testid="mobile-refresh" onClick={() => window.location.reload()} className="text-xs text-white/70" title="Refresh">
                        <RefreshCw size={18} />
                    </button>
                    <button data-testid="mobile-logout" onClick={doLogout} className="text-xs text-white/70">
                        <LogOut size={18} />
                    </button>
                </div>
            </div>

            {/* Mobile full-nav drawer (slides in from left) — gives staff/admin/vendor
                the same access to Chat, Inbox, Document Feed, etc. as desktop. */}
            {mobileDrawerOpen && user?.role !== "staff" && (
                <div
                    className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                    onClick={() => setMobileDrawerOpen(false)}
                    data-testid="mobile-drawer-backdrop"
                >
                    <div
                        className="w-72 max-w-[85vw] h-full bg-[#0A1128] text-white overflow-y-auto shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="mobile-drawer"
                    >
                        <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 bg-[#1A6177] border border-[#C5A059] rounded-full overflow-hidden">
                                    <img src="/icons/mascot-192.png" alt="" className="w-9 h-9 object-cover" />
                                </div>
                                <div>
                                    <div className="text-sm font-bold">{user?.name || "Account"}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/50">{user?.identifier} · {role}</div>
                                </div>
                            </div>
                            <button onClick={() => setMobileDrawerOpen(false)} className="p-1.5 text-white/70 hover:text-white" data-testid="mobile-drawer-close">
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="px-2 py-2">
                            {mobileDrawerItems.map(({ to, labelKey, label, icon: Icon, testid }) => {
                                const showChatBadge = to === "/chat" && unread > 0;
                                const showDocsBadge = to === "/my-files" && docsUnread > 0;
                                return (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        end={to === "/"}
                                        onClick={() => setMobileDrawerOpen(false)}
                                        data-testid={`drawer-${testid}`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2.5 rounded text-sm relative ${isActive ? "bg-[#C5A059]/10 text-[#C5A059] font-semibold" : "text-white/80 hover:bg-white/5"}`
                                        }
                                    >
                                        <Icon size={18} />
                                        <span className="flex-1">{labelKey ? t(labelKey) : label}</span>
                                        {showChatBadge && (
                                            <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                                {unread > 99 ? "99+" : unread}
                                            </span>
                                        )}
                                        {showDocsBadge && (
                                            <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                                {docsUnread > 99 ? "99+" : docsUnread}
                                            </span>
                                        )}
                                    </NavLink>
                                );
                            })}
                            <div className="border-t border-white/10 my-3"></div>
                            <NavLink
                                to="/profile"
                                onClick={() => setMobileDrawerOpen(false)}
                                className="flex items-center gap-3 px-3 py-2.5 rounded text-sm text-white/80 hover:bg-white/5"
                                data-testid="drawer-profile"
                            >
                                <Settings size={18} /> Profile & Preferences
                            </NavLink>
                            <button
                                onClick={doLogout}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm text-white/80 hover:bg-white/5"
                                data-testid="drawer-logout"
                            >
                                <LogOut size={18} /> Sign out
                            </button>
                        </nav>
                    </div>
                </div>
            )}

            <main className="flex-1 min-w-0 md:pt-0 pt-14">
                {/* Mobile bottom nav — for staff/admin/vendor, pin Chat as one of the 5
                    so they always have one-tap access. Clients keep their default order. */}
                <div
                    className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex justify-around items-center bg-[#0A1128] text-white border-t border-white/10"
                    style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                >
                    {(() => {
                        const isFirm = role === "admin" || role === "staff" || role === "vendor";
                        // Clients: fixed slim bar — Tickets · Files · Invoices · Chat · Profile
                        if (!isFirm) return clientMobileBar.length ? clientMobileBar : items.slice(0, 5);
                        // Pick the 4 most-used items (excluding chat) then always include /chat.
                        const chatItem = items.find((i) => i.to === "/chat");
                        const others = items.filter((i) => i.to !== "/chat").slice(0, 4);
                        return chatItem ? [...others, chatItem] : items.slice(0, 5);
                    })().map(({ to, labelKey, label, icon: Icon, testid }) => {
                        const showChatBadge = to === "/chat" && unread > 0;
                        const showDocsBadge = to === "/my-files" && docsUnread > 0;
                        return (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/"}
                                data-testid={`m-${testid}`}
                                className={({ isActive }) =>
                                    `flex flex-col items-center gap-0.5 py-2.5 px-2 text-[10px] flex-1 relative ${isActive ? "text-[#C5A059]" : "text-white/60"}`
                                }
                            >
                                <Icon size={18} />
                                {showChatBadge && (
                                    <span className="absolute top-1 right-3 bg-red-600 text-white text-[8px] font-bold leading-none px-1 py-0.5 rounded-full min-w-[14px] text-center animate-pulse" data-testid="m-chat-unread-badge">
                                        {unread > 99 ? "99+" : unread}
                                    </span>
                                )}
                                {showDocsBadge && (
                                    <span className="absolute top-1 right-3 bg-amber-500 text-white text-[8px] font-bold leading-none px-1 py-0.5 rounded-full min-w-[14px] text-center animate-pulse" data-testid="m-docs-unread-badge">
                                        {docsUnread > 99 ? "99+" : docsUnread}
                                    </span>
                                )}
                                <span className="truncate max-w-[60px]">{labelKey ? t(labelKey) : label}</span>
                            </NavLink>
                        );
                    })}
                </div>

                <div className="px-6 md:px-10 lg:px-14 py-8 md:py-12 max-w-[1600px] mx-auto pb-24 md:pb-12">
                    {showBackBtn && (
                        <button
                            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
                            className="mb-5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 hover:text-[#0A1128] px-2.5 py-1 rounded border border-slate-200 bg-white hover:border-slate-400 transition-colors"
                            data-testid="layout-back-btn"
                            title="Go back to the previous page"
                        >
                            <ArrowLeft size={12} /> Back
                        </button>
                    )}
                    {children}
                </div>

                <div
                    data-testid="app-copyright"
                    className="hidden md:block px-6 md:px-10 lg:px-14 py-4 text-center text-[10px] text-[#71717A] border-t border-[#E4E4E7] bg-white/60"
                >
                    © 2026 gtw.co.in. All rights reserved. gtw.co.in is owned and operated by{" "}
                    <span className="text-[#0A1128] font-medium">GHATOSKASH TAX WIZARDS PRIVATE LIMITED</span>{" "}
                    (Tax Solution is the brand name).
                </div>
            </main>
            <PreviewAsPicker open={previewOpen} onClose={() => setPreviewOpen(false)} />
            <LanguageSwitcher open={langOpen} onClose={() => setLangOpen(false)} />
        </div>
        </>
    );
}
